import { MOTION, RADIUS } from "@elyxeer/ui";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ArticleCover } from "~/components/news/ArticleCover";

/**
 * The same edge fade as the library's chip rows: a thumbnail cut in half at the
 * screen edge reads as "there is more this way" rather than as a rendering fault.
 */
const EDGE_FADE =
  "linear-gradient(to right, transparent 0, black 14px, black calc(100% - 28px), transparent 100%)";

/** Matches `Screen`'s phone gutter, which the strip bleeds through to the edges. */
const PHONE_GUTTER = 2.5;

/**
 * The article header when the article has a gallery: the cover first, then the
 * editor's photographs, browsed in the cover's own place.
 *
 * Every photo is a cover-shaped 16:9 JPEG (the API normalises gallery uploads exactly
 * like covers), so the header never changes height as the member browses and the
 * body below it stays still.
 *
 * - **The header is a scroll-snap track**, so a phone swipes through it natively —
 *   momentum, rubber-banding and all — instead of through a JS gesture handler that
 *   would fight the page's vertical scroll.
 * - **The thumbnail strip under it is the index.** It scrolls sideways on a phone
 *   and wraps on desktop, like the library's filter rows; tapping a thumbnail brings
 *   that photo into the header. The active one is ringed.
 * - **Desktop gets arrows**, because a mouse has no swipe. They are CSS-hidden on a
 *   phone rather than branched on in JS, so the first paint is already right.
 *
 * The active index is read back from the track's scroll position, so a swipe, an
 * arrow, a thumbnail and a keyboard scroll all end up in the same state.
 *
 * With a single image there is nothing to browse, and this is exactly `ArticleCover`.
 */
export function ArticleGallery({
  slug,
  images,
}: {
  slug: string;
  /** The cover (when there is one) followed by the gallery, in the editor's order. */
  images: string[];
}) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);

  if (images.length <= 1) {
    return <ArticleCover slug={slug} src={images[0] ?? null} alt="" />;
  }

  const last = images.length - 1;

  // Brings the active thumbnail into the strip's view on a phone, where it scrolls.
  // Measured by hand: `scrollIntoView` would also scroll the page vertically.
  const revealThumbnail = (index: number) => {
    const strip = stripRef.current;
    const item = strip?.children[index] as HTMLElement | undefined;
    if (!strip || !item) return;
    const start = item.offsetLeft - strip.offsetLeft;
    const end = start + item.offsetWidth;
    if (start < strip.scrollLeft) {
      strip.scrollTo({ left: start - 24 });
    } else if (end > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ left: end - strip.clientWidth + 32 });
    }
  };

  const goTo = (index: number) => {
    const track = trackRef.current;
    const next = Math.min(Math.max(index, 0), last);
    setActive(next);
    revealThumbnail(next);
    // The track's CSS `scroll-behavior` animates this, and drops the animation
    // for a member who asked for reduced motion.
    track?.scrollTo({ left: next * track.clientWidth });
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index !== active) {
      setActive(index);
      revealThumbnail(index);
    }
  };

  return (
    <Box
      component="section"
      aria-roledescription={t("article.gallery.roleDescription")}
      aria-label={t("article.gallery.label")}
      data-testid="article.gallery"
      sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, md: 2 } }}
    >
      <Box sx={{ position: "relative" }}>
        <Box
          ref={trackRef}
          onScroll={onScroll}
          // Focusable, so the arrow keys scroll it for a keyboard user.
          tabIndex={0}
          data-testid="article.gallery-track"
          sx={{
            display: "flex",
            overflowX: "auto",
            overflowY: "hidden",
            scrollSnapType: "x mandatory",
            scrollBehavior: "smooth",
            overscrollBehaviorX: "contain",
            borderRadius: `${RADIUS.lg}px`,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
            },
            "@media (prefers-reduced-motion: reduce)": {
              scrollBehavior: "auto",
            },
          }}
        >
          {images.map((src, index) => (
            <Box
              key={src}
              role="group"
              aria-roledescription={t("article.gallery.slide")}
              aria-label={t("article.gallery.position", {
                index: index + 1,
                count: images.length,
              })}
              aria-hidden={index === active ? undefined : true}
              data-testid={`article.gallery-slide-${index}`}
              sx={{
                flex: "0 0 100%",
                scrollSnapAlign: "start",
                scrollSnapStop: "always",
              }}
            >
              {/* The track owns the rounding, so a slide in mid-swipe is square-edged
                where it meets its neighbour rather than showing a notch. */}
              <ArticleCover
                slug={slug}
                src={src}
                alt=""
                sx={{ borderRadius: 0 }}
              />
            </Box>
          ))}
        </Box>

        <Typography
          variant="L3"
          aria-hidden
          data-testid="article.gallery-counter"
          sx={{
            position: "absolute",
            top: { xs: 10, md: 16 },
            right: { xs: 10, md: 16 },
            paddingInline: 1,
            paddingBlock: 0.25,
            borderRadius: `${RADIUS.full}px`,
            // Over a photograph, so the media scrim, like the format badge on a tile.
            backgroundColor: "mediaScrim.main",
            backdropFilter: "blur(4px)",
            color: "mediaScrim.contrastText",
            fontVariantNumeric: "tabular-nums",
            pointerEvents: "none",
          }}
        >
          {active + 1} / {images.length}
        </Typography>

        <GalleryArrow
          side="left"
          label={t("article.gallery.previous")}
          disabled={active === 0}
          onClick={() => goTo(active - 1)}
        />
        <GalleryArrow
          side="right"
          label={t("article.gallery.next")}
          disabled={active === last}
          onClick={() => goTo(active + 1)}
        />
      </Box>

      <Box
        component="ul"
        ref={stripRef}
        aria-label={t("article.gallery.thumbnails")}
        data-testid="article.gallery-thumbnails"
        sx={{
          listStyle: "none",
          margin: 0,
          // Bleeds through the page gutter on a phone so the strip runs to the
          // screen edges; the padding puts the first thumbnail back in line.
          marginInline: { xs: -PHONE_GUTTER, md: 0 },
          paddingInline: { xs: PHONE_GUTTER, md: 0 },
          // Room for the active ring, which the scroll container would clip.
          paddingBlock: "3px",
          display: "flex",
          gap: 1,
          flexWrap: { xs: "nowrap", md: "wrap" },
          overflowX: { xs: "auto", md: "visible" },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          maskImage: { xs: EDGE_FADE, md: "none" },
          WebkitMaskImage: { xs: EDGE_FADE, md: "none" },
        }}
      >
        {images.map((src, index) => {
          const current = index === active;
          return (
            <Box component="li" key={src} sx={{ flexShrink: 0 }}>
              <ButtonBase
                onClick={() => goTo(index)}
                aria-label={t("article.gallery.show", {
                  index: index + 1,
                  count: images.length,
                })}
                aria-current={current ? "true" : undefined}
                data-testid={`article.gallery-thumb-${index}`}
                sx={{
                  display: "block",
                  width: { xs: 88, md: 104 },
                  aspectRatio: "16 / 9",
                  borderRadius: `${RADIUS.sm}px`,
                  overflow: "hidden",
                  backgroundColor: "surface.inset",
                  outline: "2px solid",
                  outlineOffset: 1,
                  outlineColor: current ? "primary.main" : "transparent",
                  opacity: current ? 1 : 0.6,
                  transition: `opacity ${MOTION.transition}, outline-color ${MOTION.transition}`,
                  "&:hover": { opacity: 1 },
                  "&.Mui-focusVisible": { outlineColor: "primary.main" },
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function GalleryArrow({
  side,
  label,
  disabled,
  onClick,
}: {
  side: "left" | "right";
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon =
    side === "left" ? ChevronLeftRoundedIcon : ChevronRightRoundedIcon;

  return (
    <IconButton
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      data-testid={`article.gallery-${side === "left" ? "previous" : "next"}`}
      sx={{
        // A phone swipes; the arrows are for a pointer. CSS, not a media-query
        // hook, so the first paint is already right.
        display: { xs: "none", md: "inline-flex" },
        position: "absolute",
        top: "50%",
        [side]: 16,
        transform: "translateY(-50%)",
        backgroundColor: "mediaScrim.main",
        color: "mediaScrim.contrastText",
        backdropFilter: "blur(4px)",
        transition: `opacity ${MOTION.transition}`,
        "&:hover": { backgroundColor: "mediaScrim.main", opacity: 0.9 },
        "&.Mui-disabled": { opacity: 0, pointerEvents: "none" },
      }}
    >
      <Icon />
    </IconButton>
  );
}
